/**
 * REVIEW-MESSAGE.WIDGET
 * One point of the review of a project (or of a questionnaire) : a tinted line with its dot and an arrow, a click goes to the place to fix
 * The message has its **bold** parts, and an example at the end ("(par exemple …)") that is lighter
 */
import { ReactNode } from "react"
import { motion } from "framer-motion"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { IconDefinition } from "@fortawesome/fontawesome-common-types"
import { faArrowRight, faWandMagicSparkles } from "@fortawesome/free-solid-svg-icons"
import renderBold from "@/utils/render-bold.utils"
import AnimatedColorSvgWidget from "@/widgets/animated-color-svg.widget"
import getEmoji from "@/utils/get-emoji.utils"
import { useAnimationsDisabled } from "@/utils/animation-mode.utils"

interface Props {
  text: string //Message, already translated
  kind: "error" | "warning"
  onClick: Function
  details?: string[] //Lines under the message (what is missing, where)
  languages?: string[] //Languages in which a text is missing : one chip each (flag and code), a click goes to that language
  onLanguage?: (language: string) => void
  action?: ReactNode //At the right, before the arrow (ex: ignore a warning)
  isAi?: boolean //The message comes from the AI : a small logo before the text
  index?: number //Order of appearance (delay of the animation)
}

const COLOR = { error: "var(--idt-red)", warning: "var(--idt-orange)" }

export const tint = (color: string): string => `color-mix(in srgb, ${color} 9%, white)`

//"**Title** : sentence" : the title without its bold marks, and the sentence (null when there is no title)
function splitTitle(text: string): { title: string; rest: string } | null {
  const [title, ...rest] = text.split(" : ")
  return rest.length > 0 ? { title: title.replace(/\*\*/g, ""), rest: rest.join(" : ") } : null
}

//Buttons of a point of the AI : same pill for all of them (the main one in orange with the wand of the AI, the other one muted)
export function AiActionButton(props: { text: string; onClick: () => void; icon?: IconDefinition; isMuted?: boolean }) {
  const color: string = props.isMuted ? "#8c8c8c" : COLOR.warning
  return (
    <span
      className="_hover text-sm flex cursor-pointer"
      onClick={props.onClick}
      style={{
        alignItems: "center",
        gap: 6,
        padding: "3px 12px",
        borderRadius: 999,
        border: "1px solid " + color,
        background: "white",
        color,
        fontWeight: 600,
        whiteSpace: "nowrap"
      }}>
      {props.icon ? (
        <FontAwesomeIcon icon={props.icon} />
      ) : (
        <span className="list-icon-animation">
          <AnimatedColorSvgWidget />
          <FontAwesomeIcon icon={faWandMagicSparkles} />
        </span>
      )}
      {props.text}
    </span>
  )
}

//Small logo of the AI (the wand in the animated gradient of the AI skin) : the point comes from the suggestions of the AI
export function AiMark() {
  return (
    <span className="list-icon-animation" style={{ display: "inline-block", marginRight: 8 }}>
      <AnimatedColorSvgWidget />
      <FontAwesomeIcon icon={faWandMagicSparkles} />
    </span>
  )
}

export default function ReviewMessage(props: Props) {
  const isAnimationsDisabled = useAnimationsDisabled()
  const color: string = COLOR[props.kind]
  const [, main, example] = props.text.match(/^([\s\S]*?)(\s*\([^()]*\)\.?)$/) || [null, props.text, ""]
  //A point of the AI : the title (orange, with the logo) then the sentence
  const ai = props.isAi ? splitTitle(props.text) : null

  return (
    <motion.div
      initial={isAnimationsDisabled ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, delay: isAnimationsDisabled ? 0 : (props.index || 0) * 0.06 }}
      whileHover={isAnimationsDisabled ? undefined : { x: 3 }}
      onClick={() => props.onClick()}
      className={"flex cursor-pointer " + (props.kind === "error" ? "red-t" : "dark-t")}
      style={{
        gap: 14,
        alignItems: "center",
        padding: "16px 28px 16px 22px",
        marginBottom: 14,
        borderRadius: 12,
        fontSize: 14,
        lineHeight: 1.45,
        background: tint(color)
      }}>
      <span style={{ width: 8, height: 8, flexShrink: 0, borderRadius: "50%", background: color }} />
      <div>
        {ai ? (
          <>
            <div style={{ color: COLOR.warning, fontWeight: 700 }}>
              <AiMark />
              {renderBold(ai.title)}
            </div>
            <div style={{ marginTop: 2 }}>
              {renderBold(ai.rest.replace(/«[^»]*»/g, (quote) => "**" + quote + "**"))}
            </div>
          </>
        ) : (
          <div>{renderBold(main)}</div>
        )}
        {example && (
          <div className="grey-t text-sm" style={{ fontStyle: "italic", marginTop: 2 }}>
            {example.trim()}
          </div>
        )}
        {props.details?.map((detail, i) => (
          <div key={i} className="grey-t text-sm">
            {renderBold(detail)}
          </div>
        ))}
        {/* A point of the AI : its buttons are under the text (the arrow stays on the right) */}
        {ai && props.action && <div style={{ marginTop: 10 }}>{props.action}</div>}
      </div>
      <div className="flex1" />
      {props.languages && props.languages.length > 0 && (
        <div className="flex" style={{ gap: 6, flexShrink: 0 }}>
          {props.languages.map((language) => (
            <span
              key={language}
              className="text-sm"
              onClick={(event) => {
                event.stopPropagation()
                props.onLanguage?.(language)
              }}
              style={{
                padding: "2px 10px",
                borderRadius: 999,
                border: "1px solid " + color,
                background: "white",
                fontWeight: 600,
                whiteSpace: "nowrap"
              }}>
              {getEmoji(language)} {language.toUpperCase()}
            </span>
          ))}
        </div>
      )}
      {!ai && props.action}
      <FontAwesomeIcon icon={faArrowRight} style={{ color, flexShrink: 0 }} />
    </motion.div>
  )
}

interface GroupProps {
  kind: "error" | "warning"
  text: string //What is wrong, the same for all the items (already translated)
  items: { label: string; onClick: Function }[] //What it is about, one link each
}

//The same point found on several places (several questions without text) : said once, with a link to each place
export function ReviewMessageGroup(props: GroupProps) {
  const isAnimationsDisabled = useAnimationsDisabled()
  const color: string = COLOR[props.kind]
  const [, main, example] = props.text.match(/^([\s\S]*?)(\s*\([^()]*\)\.?)$/) || [null, props.text, ""]

  return (
    <motion.div
      initial={isAnimationsDisabled ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className={props.kind === "error" ? "red-t" : "dark-t"}
      style={{
        padding: "16px 28px 16px 22px",
        marginBottom: 14,
        borderRadius: 12,
        fontSize: 14,
        lineHeight: 1.45,
        background: tint(color)
      }}>
      {/* One line when there is room (the links follow the title), the links go to the next line when there is not */}
      <div
        className="flex"
        style={{ flexWrap: "wrap", alignItems: "center", gap: "8px 10px", maxHeight: 140, overflowY: "auto" }}>
        <span
          className="flex text-sm"
          style={{
            minWidth: 24,
            height: 24,
            padding: "0 6px",
            borderRadius: 999,
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            fontWeight: 700,
            color: "white",
            background: color
          }}>
          {props.items.length}
        </span>
        <span style={{ marginRight: 6 }}>
          {renderBold(main)}
          {example && (
            <span className="grey-t" style={{ fontStyle: "italic" }}>
              {example}
            </span>
          )}
        </span>
        {props.items.map((item, i) => (
          <span
            key={i}
            className="cursor-pointer text-sm"
            onClick={() => item.onClick()}
            style={{
              padding: "3px 10px",
              borderRadius: 999,
              border: "1px solid " + color,
              background: "white",
              fontWeight: 600
            }}>
            {item.label}
            <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: 6 }} />
          </span>
        ))}
      </div>
    </motion.div>
  )
}
